// Kamerakalibrering: fra en PIKSEL i et kamerabilde til en PEILING i terrenget.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// Et fast kamera (Frigate, PTZ, trafikkamera) melder «person i boks
// [424,500,536,712]». Uten kalibrering er det bare «noe skjedde ved kamera NØ» —
// nyttig, men ikke en posisjon. Med monteringsposisjon, asimut og synsfelt blir
// samme boks en PEILING, og med en høydemodell et bakkepunkt.
//
// ⚠ UKALIBRERT KAMERA SKAL LEVERE SEKTOR, IKKE PUNKT. Et kamera ingen har målt
// inn vet vi retningen på med ±180°. Å plassere en markør da ville vært å dikte.
// Kalibreringen er FELTARBEID (kompass + GNSS per kamera), ikke noe kode kan
// gjette seg til — se `vale/SENSORLAG_MULIGHETSSTUDIE_2026-08-17.md` §5.
import { z } from 'zod';
import { project } from './geo.js';
import { TrackOrgSchema } from './track.js';
/**
 * Ett innmålt kamera.
 *
 * ASIMUT ER GRADER SANN. Samme krav som overalt ellers i sensorlaget: leses et
 * kompassavlest (magnetisk) tall inn her, blir alle peilinger fra kameraet skjevt
 * med deklinasjonen. Bruk `magdec.ts` ved innmåling.
 */
export const KamerakalibreringSchema = z.object({
    /** Må matche kameranavnet kilden bruker (Frigates `camera`-felt). */
    kameraId: z.string().min(1).max(120),
    navn: z.string().max(120).optional(),
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    /** Kameraets høyde over BAKKEN der det står (mastehøyde). */
    hoydeOverBakkenM: z.number().min(0).max(500).optional(),
    /** Retningen midt i bildet peker, grader SANN. */
    asimutDeg: z.number().min(0).max(360),
    /**
     * Horisontalt synsfelt i grader. Fra kameraets datablad eller målt inn ved å
     * peile to gjenkjennelige punkt i bildekantene.
     */
    hfovDeg: z.number().positive().max(180),
    vfovDeg: z.number().positive().max(180).optional(),
    /** Nedvinkel: negativ = ned mot bakken. Trengs for avstand, ikke for peiling. */
    pitchDeg: z.number().min(-90).max(90).optional(),
    /** Bildebredde i piksler — nødvendig for å tolke en boks-koordinat. */
    breddePx: z.number().int().positive().max(100_000),
    hoydePx: z.number().int().positive().max(100_000).optional(),
    /**
     * 1σ usikkerhet i monteringsasimuten (grader). Et kamera pekt inn med
     * håndkompass er ikke bedre enn ~3–5°; ett innmålt mot to kjente punkt kan
     * være under 1°. Udefinert betyr IKKE 0 — kalleren skal da falle tilbake på
     * halve synsfeltet som usikkerhet.
     */
    asimutSigmaDeg: z.number().min(0).max(180).optional(),
    /** Rekkevidde kameraet realistisk ser noe brukbart på (m). */
    rekkeviddeM: z.number().positive().max(50_000).optional(),
    /**
     * Org-tilhørighet for RBAC-scope (§4 default deny).
     *
     * ⚠ HØRER PÅ KALIBRERINGEN, IKKE PÅ DETEKSJONEN. Den som fysisk måler inn
     * kameraet vet hvilket distrikt og område det står i; en deteksjon som kommer
     * inn i sanntid gjør ikke det. Første utgave hadde en oppslagsfunksjon i hubben
     * som ALLTID returnerte undefined — en stub som lot seg lese som en virkende
     * tilgangskontroll. Utelates feltet, faller hubben tilbake på sitt eget
     * distrikt, slik at deteksjonen er synlig for noen framfor for ingen.
     */
    org: TrackOrgSchema.optional(),
});
/** Standard rekkevidde når kalibreringen ikke oppgir noe (tegnelengde). */
export const KAMERA_REKKEVIDDE_DEFAULT_M = 300;
/**
 * Vinkel (grader, ± fra kameraets MIDTAKSE — ikke en sann peiling) for en
 * pikselkolonne, REKTILINEÆRT.
 *
 * ⚠ REKTILINEÆR PROJEKSJON, IKKE LINEÆR INTERPOLASJON.
 * Den nærliggende feilen er `(x/bredde - 0.5) * hfov`. Den er GAL for alt annet
 * enn svært smale linser, fordi et kamera projiserer på et PLAN og ikke på en
 * kuleflate: vinkelen per piksel er størst midt i bildet og minst i kanten.
 *
 * Riktig: f = (bredde/2) / tan(hfov/2), deretter vinkel = atan((x − cx) / f).
 *
 * Størrelsen på feilen, for å vise at det ikke er pedanteri: med hfov 90° og et
 * objekt helt ute i bildekanten gir lineær interpolasjon 45,0° mens sannheten er
 * 45,0° (kantene stemmer alltid) — men PÅ HALVVEIEN UT (x = 0,75·bredde) gir
 * lineær 22,5° mot rektilineært 26,6°. Fire graders feil på 300 m er 21 m, og
 * det er forskjellen på riktig og feil hus.
 *
 * FAKTORERT UT av `peilingFraPiksel` (bølge MAAL-01) fordi landemerke-resection
 * (`maalpeking.ts`) trenger NØYAKTIG samme uttrykk i REVERS: gitt et landemerke
 * med KJENT sann peiling og dets pikselkolonne, løses monterings-asimuten
 * (`asimut = sannPeiling − vinkel`) ut i stedet for at asimuten er kjent og
 * vinkelen søkes. Én formel, to bruksmåter — å duplisere den i `maalpeking.ts`
 * hadde brutt §3 (én kilde til sannhet) og latt de to filene drifte fra hverandre
 * første gang én av dem ble «forbedret».
 */
export function vinkelFraPikselKolonne(k, xPiksel) {
    const bredde = k.breddePx;
    const cx = bredde / 2;
    // Brennvidde i piksler, utledet av synsfeltet.
    const f = cx / Math.tan((k.hfovDeg * Math.PI) / 360); // hfov/2 i radianer
    return (Math.atan((xPiksel - cx) / f) * 180) / Math.PI;
}
/**
 * Peiling (grader sann) til en pikselkolonne i bildet. Se `vinkelFraPikselKolonne`
 * for selve projeksjonen — denne legger bare monterings-asimuten til og wrapper.
 */
export function peilingFraPiksel(k, xPiksel) {
    const vinkel = vinkelFraPikselKolonne(k, xPiksel);
    return ((k.asimutDeg + vinkel) % 360 + 360) % 360;
}
/**
 * Vinkel (grader, ± fra kameraets OPTISKE AKSE — ikke en elevasjon) for en
 * pikselrad, REKTILINEÆRT. Samme faktorering som `vinkelFraPikselKolonne`, av
 * samme grunn: `maalpeking.ts` sin maritime metode (horisont+havflate) trenger
 * VINKELEN mellom to pikselrader (horisontlinje og målets vannlinje) UTEN å vite
 * — eller bry seg om — monteringens pitch, fordi pitch kanselleres når to
 * elevasjoner fra SAMME kalibrering trekkes fra hverandre. Å be om en full
 * `Kamerakalibrering` (med asimut, breddePx osv.) bare for å ta den differansen
 * ville vært å late som mer var kjent enn det som faktisk trengs.
 *
 * Returnerer `null` når `vfovDeg`/`hoydePx` mangler — uten dem er det ingenting
 * å regne av, og en gjettet vertikalvinkel gir en gjettet avstand.
 */
export function vinkelFraPikselRad(k, yPiksel) {
    if (k.vfovDeg === undefined || k.hoydePx === undefined)
        return null;
    const cy = k.hoydePx / 2;
    const f = cy / Math.tan((k.vfovDeg * Math.PI) / 360);
    // Piksel-y vokser NEDOVER i bildet, mens vinkelen (som elevasjon) vokser oppover.
    return (-Math.atan((yPiksel - cy) / f) * 180) / Math.PI;
}
/**
 * Elevasjonsvinkel (grader, negativ = ned) til en pikselrad. Se
 * `vinkelFraPikselRad` for selve projeksjonen — denne legger monteringens
 * pitch til.
 */
export function elevasjonFraPiksel(k, yPiksel) {
    const vinkel = vinkelFraPikselRad(k, yPiksel);
    if (vinkel === null)
        return null;
    return (k.pitchDeg ?? 0) + vinkel;
}
/**
 * Peiling til boksens NEDRE MIDTPUNKT.
 *
 * ⚠ NEDRE KANT, IKKE SENTER — OG DET ER EN OPERATIV FORSKJELL.
 * Boksens nedre kant er der objektet møter bakken (føtter, hjul, vannlinje).
 * Det er DET punktet som ligger på terrengflata og dermed kan geolokaliseres.
 * Bruker man boksens senter, peker strålen på objektets midje og treffer
 * terrenget for langt bak — systematisk, og verst for høye objekter nær kameraet.
 */
export function peilingTilBoks(k, boks) {
    const [x1, , x2] = boks;
    return peilingFraPiksel(k, (x1 + x2) / 2);
}
/** Elevasjon til boksens nedre kant (bakkekontakten). Null uten vertikal-kalibrering. */
export function elevasjonTilBoks(k, boks) {
    const [, , , y2] = boks;
    return elevasjonFraPiksel(k, y2);
}
/**
 * Halv sektorbredde for en deteksjon fra dette kameraet.
 *
 * Rangeringen er den samme ærlighetsrekkefølgen som for feltbilder: oppgitt
 * monterings-usikkerhet først, ellers halve synsfeltet — som er det ærlige svaret
 * «et sted i bildet» når ingen har målt inn kameraet nøyaktig.
 */
export function kameraSektorHalvbreddeDeg(k) {
    if (k.asimutSigmaDeg !== undefined && k.asimutSigmaDeg > 0)
        return k.asimutSigmaDeg;
    return k.hfovDeg / 2;
}
/**
 * Grovt bakkepunkt UTEN høydemodell, ved å anta flat bakke.
 *
 * ⚠ BRUK MED ÅPNE ØYNE. Antakelsen «flat bakke i kameraets egen høyde» holder på
 * en havn, en parkeringsplass eller en veistrekning, og bryter i kupert lende.
 * Funksjonen finnes fordi et fast overvåkingskamera oftest DEKKER en flate som
 * faktisk er flat — men returnerer `null` så snart forutsetningene mangler
 * (ingen vertikal-kalibrering, eller en stråle som ikke peker nedover), i stedet
 * for å strekke antakelsen. Har du DEM, bruk `estimerMaal` i `maalestimat.ts`.
 */
export function flatbakkePunkt(k, boks) {
    const h = k.hoydeOverBakkenM;
    if (h === undefined || h <= 0)
        return null;
    const elev = elevasjonTilBoks(k, boks);
    if (elev === null || elev >= 0)
        return null; // peker vannrett/opp → ingen skjæring
    const avstandM = h / Math.tan((-elev * Math.PI) / 180);
    if (!Number.isFinite(avstandM) || avstandM <= 0)
        return null;
    return {
        punkt: project({ lat: k.lat, lon: k.lon }, peilingTilBoks(k, boks), avstandM),
        avstandM,
    };
}
//# sourceMappingURL=kamerakalibrering.js.map